// Copyright Abridged, Inc. 2021,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {NearToEthTransfer} from '../models/near-to-eth-transfer.model.js';
import {TokenOfferAcceptance} from '../models/token-offer-acceptance.model.js';
import {TokenSale} from '../models/token-sale.model.js';
import {TokenOffer} from '../models/token-offer.model.js';
import {TokenItem} from '../models/token-item.model.js';
import {TokenListing} from '../models/token-listing.model.js';
import {MarketContractSettings} from '../models/market-contract-settings.model.js';
import {TokenContract} from '../models/token-contract.model.js';
import {Nep171TokenTypes} from '../models/nep-171-token-types.model.js';
import {TokenSettings} from '../models/token-settings.model.js';
import {TokenContractSettings} from '../models/token-contract-settings.model.js';
import {TokenTransfer} from '../models/token-transfer.model.js';
import {Token} from '../models/token.model.js';
import {UserAccount} from '../models/user-account.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by NearNFTController.
 */
export interface NearNFTService {
  /**
   * @param xNearSignature
   * @param _requestBody
   */
  nearToEth(
    xNearSignature: string | undefined,
    _requestBody: NearToEthTransfer,
  ): Promise<unknown>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  accept(
    xNearSignature: string | undefined,
    _requestBody: TokenOfferAcceptance,
  ): Promise<TokenSale>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  buy(
    xNearSignature: string | undefined,
    _requestBody: TokenOffer,
  ): Promise<TokenSale>;

  /**
   * @param _requestBody
   * @returns OK
   */
  getSale(_requestBody: TokenItem): Promise<TokenListing>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  removeSale(
    xNearSignature: string | undefined,
    _requestBody: TokenSale,
  ): Promise<TokenSale>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  sell(
    xNearSignature: string | undefined,
    _requestBody: TokenSale,
  ): Promise<TokenSale>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  updatePrice(
    xNearSignature: string | undefined,
    _requestBody: TokenSale,
  ): Promise<TokenSale>;

  /**
   * @param address
   * @param _requestBody
   */
  addFtTokenIds(address: string, _requestBody: string[]): Promise<unknown>;

  /**
   * @param marketContract
   * @param tokenContract
   * @param tokenId
   * @returns OK
   */
  getSaleByTokenId(
    marketContract: string,
    tokenContract: string,
    tokenId: string,
  ): Promise<TokenListing>;

  /**
   * @param marketContract
   * @param tokenContract
   * @param offset
   * @param limit
   * @returns OK
   */
  getSalesByTokenContract(
    marketContract: string,
    tokenContract: string,
    offset?: number,
    limit?: number,
  ): Promise<TokenListing[]>;

  /**
   * @param marketContract
   * @param indexName
   * @param indexKey
   * @param offset
   * @param limit
   * @returns OK
   */
  getSales(
    marketContract: string,
    indexName: 'owner_id' | 'nft_contract_id' | 'nft_token_type',
    indexKey: string,
    offset?: number,
    limit?: number,
  ): Promise<TokenListing[]>;

  /**
   * @param marketContract
   * @param seller
   * @param offset
   * @param limit
   * @returns OK
   */
  getSalesBySeller(
    marketContract: string,
    seller: string,
    offset?: number,
    limit?: number,
  ): Promise<TokenListing[]>;

  /**
   * @param marketContract
   * @param tokenType
   * @param offset
   * @param limit
   * @returns OK
   */
  getSalesByTokenType(
    marketContract: string,
    tokenType: string,
    offset?: number,
    limit?: number,
  ): Promise<TokenListing[]>;

  /**
   * @param _requestBody
   * @returns OK
   */
  createMarketContract(
    _requestBody: MarketContractSettings,
  ): Promise<TokenContract>;

  /**
   * @param collectionId
   * @returns Result set of TokenContract model instances
   */
  getTokensByCollectionId(collectionId: string): Promise<{
    paginationToken?: string;
    items?: TokenContract[];
  }>;

  /**
   * @param address
   * @param _requestBody
   */
  unlock(address: string, _requestBody: string[]): Promise<unknown>;

  /**
   * @param address
   * @param _requestBody
   */
  addTokenTypes(
    address: string,
    _requestBody: Nep171TokenTypes,
  ): Promise<unknown>;

  /**
   * @param address Token contract address
   * @param receiverId Account that receives the token
   * @param _requestBody
   * @returns OK
   */
  mintToken(
    address: string,
    receiverId: string | undefined,
    _requestBody: TokenSettings,
  ): Promise<{
    tokenId?: string;
    transactionHash?: string;
  }>;

  /**
   * @param address Token contract address
   * @returns OK
   */
  getTokenContract(address: string): Promise<TokenContract>;

  /**
   * @param _requestBody
   * @returns OK
   */
  createTokenContract(
    _requestBody: TokenContractSettings,
  ): Promise<TokenContract>;

  /**
   * @param xNearSignature
   * @param _requestBody
   * @returns OK
   */
  transfer(
    xNearSignature: string | undefined,
    _requestBody: TokenTransfer,
  ): Promise<Token>;

  /**
   * @param address Token contract address
   * @param id Token id
   * @returns OK
   */
  getTokenById(address: string, id: string): Promise<Token>;

  /**
   * @param address Token contract address
   * @param limit Maximum number of items to be returned
   * @param paginationToken Pagination token from last query
   * @returns Result set of Token model instances
   */
  getTokens(
    address: string,
    limit?: number,
    paginationToken?: string,
  ): Promise<{
    paginationToken?: string;
    items?: Token[];
  }>;

  /**
   * @param userId User account id
   * @param erc20 ERC20 token address or symbol
   * @returns OK
   */
  getBalance(
    userId: string,
    erc20?: string,
  ): Promise<{
    balance?: string;
  }>;

  /**
   * @param userId User account id
   * @param address Token contract address
   * @param limit Maximum number of items to be returned
   * @param paginationToken Pagination token from last query
   * @returns Result set of TokenContract model instances
   */
  getOwnedTokensByContract(
    userId: string,
    address: string,
    limit?: number,
    paginationToken?: string,
  ): Promise<{
    paginationToken?: string;
    items?: TokenContract[];
  }>;

  /**
   * @param userId User account id
   * @returns Result set of TokenContract model instances
   */
  getOwnedTokens(userId: string): Promise<{
    paginationToken?: string;
    items?: TokenContract[];
  }>;

  /**
   * @param userId User account id
   * @param limit Maximum number of items to be returned
   * @param paginationToken Pagination token from last query
   * @returns Result set of TokenContract model instances
   */
  getCreatedTokenContracts(
    userId: string,
    limit?: number,
    paginationToken?: string,
  ): Promise<{
    paginationToken?: string;
    items?: TokenContract[];
  }>;

  /**
   * @param xNearSignature
   * @param _requestBody NEAT user account
   * @returns OK
   */
  registerUser(
    xNearSignature: string | undefined,
    _requestBody: UserAccount,
  ): Promise<UserAccount>;
}

export class NearNFTServiceProvider implements Provider<NearNFTService> {
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<NearNFTService> {
    const service = await getService<{
      apis: {NearNFTController: NearNFTService};
    }>(this.dataSource);
    return service.apis['NearNFTController'];
  }
}
